import { appConfigStore } from 'Models/AppConfigStore';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { useLocation } from 'react-router-dom';
import { getMessageByKey } from 'Stores/LanguageStore';
import { BookingWizardData } from 'Views/Components/_HumanWritten/BookingWizard/FerryTripBookingWizard/BookingWizardData/BaseBookingWizardData';
import { getTotalPassengerCountFromWizardData } from 'Views/Components/_HumanWritten/BookingWizard/FerryTripBookingWizard/WizardSteps/Passengers/PassengerTabHelpers';
import If from 'Views/Components/If/If';
import { stringNotEmpty } from 'Util/TypeGuards';
import { wizardModeOptions } from 'Models/Enums';
import { BookingTypeButtons } from './BookingTypeButtons';
import { PassengerTypeInputs } from './PassengerTypeInputs';
import { FerryFilterSubheading } from './FerryFilterSubheading';
import { useBookingWizardDataStore } from '../../../BookingWizardDataStore';
import { MessageBox } from 'Views/Components/_HumanWritten/MessageBox/MessageBox';

export type UpdateWizardData = (wizardData: BookingWizardData) => void;

export interface FerryFilterPassengersProps {
	excludeBookingTypeButton?: boolean;
}

export function FerryFilterPassengers({ excludeBookingTypeButton }: FerryFilterPassengersProps) {
	const location = useLocation();
	const { wizardData } = useBookingWizardDataStore();

	const { tabSelected, wizardMode } = wizardData;

	const editMode = wizardMode === wizardModeOptions.ALTERATION;
	const onVehicleTab = tabSelected === 'vehicle';
	const onPassengerTab = tabSelected === 'passenger';

	const passengerCount = getTotalPassengerCountFromWizardData(wizardData);

	// Admin-configurable banners shown in the passenger count sidebar. The text is driven by the
	// language/translation system; each banner is hidden whenever its translation is empty. The
	// walk-on variant shows on the walk-on (passenger) tab, and the vehicle variant on the vehicle tab.
	// The key is resolved here to decide whether to render at all, then handed to FormattedMessage so
	// the translation can emphasise part of the sentence with <strong> tags.
	let passengerBannerKey: string | null = null;
	if (onPassengerTab) {
		passengerBannerKey = 'walk-on-passenger-count__banner__text';
	} else if (onVehicleTab) {
		passengerBannerKey = 'vehicle-passenger-count__banner__text';
	}
	const passengerBannerText = passengerBannerKey !== null ? (getMessageByKey(passengerBannerKey, '') ?? '') : '';

	return (
		<div className="passenger-details__container">
			<If condition={excludeBookingTypeButton !== true}>
				<BookingTypeButtons editMode={editMode} />
			</If>
			<FerryFilterSubheading
				onPassengerTab={onPassengerTab}
				onVehicleTab={onVehicleTab}
				passengerButtonSubheading={getMessageByKey(
					'passenger-booking__button-selection__subheading',
					'Walk-on Passengers',
				)}
				vehicleButtonSubheading={getMessageByKey(
					'vehicle-booking__button-selection__subheading',
					'Vehicle & Passengers (including driver)',
				)}
				trailersEnabled={appConfigStore.trailersEnabled}
				passengerCount={passengerCount}
			/>
			{passengerBannerKey !== null && stringNotEmpty(passengerBannerText.trim()) && (
				<MessageBox status="info" slim noBottomMargin>
					{
						<p>
							<FormattedMessage
								id={passengerBannerKey}
								defaultMessage=""
								values={{ strong: (chunks: React.ReactNode) => <strong>{chunks}</strong> }}
							/>
						</p>
					}
				</MessageBox>
			)}
			<If condition={onPassengerTab || (onVehicleTab && appConfigStore.vehicleHasPassengersEnabled)}>
				<PassengerTypeInputs location={location} />
			</If>
		</div>
	);
}
